// ChatMessage.tsx
import React, { useState } from 'react';
import { Comment, Message, Label, Icon, Button } from 'semantic-ui-react';
import moment from 'moment';
import Markdown  from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw'

interface ChatMessageProps {
  message: {
    id: string;
    timestamp: string;
    type: 'user' | 'assistant' | 'system';
    content: string;
    metadata?: any;
  };
}

const ChatMessage: React.FC<ChatMessageProps> = ({ message }) => {
  const [showThinking, setShowThinking] = useState(false);
  const { type, content, timestamp, metadata } = message;

  // Format timestamp
  const formattedTime = moment(timestamp).format('h:mm A');

  // Check if content contains HTML tables
  const hasHTMLTable = content && content.includes('<table');

  // Render different message types
  switch (type) {
    case 'user':
      return (
        <Comment className="user-message">
          {/* <Comment.Avatar src='/avatar-user.png' /> */}
          <Comment.Content>
            <Comment.Author as='span'>You</Comment.Author>
            <Comment.Metadata>
              <div>{formattedTime}</div>
            </Comment.Metadata>
            <Comment.Text>{content}</Comment.Text>
          </Comment.Content>
        </Comment>
      );

    case 'assistant':
      return (
        <Comment className="assistant-message">
          {/* <Comment.Avatar src='/avatar-bot.png' /> */}
          <Comment.Content>
            <Comment.Author as='span'>
              IsHealth Ai
              {metadata?.streaming && (
                <Icon name="circle notched" loading size="small" style={{ marginLeft: '0.5em' }} />
              )}
              {metadata?.summary && !metadata?.streaming && (
                <Label size="mini" color="blue" style={{ marginLeft: '0.5em' }}>
                  <Icon name="clipboard list" />
                  Summary
                </Label>
              )}
            </Comment.Author>
            <Comment.Metadata>
              <div>{formattedTime}</div>
              {metadata?.thinking && (
                <Button
                  size="mini"
                  basic
                  onClick={() => setShowThinking(!showThinking)}
                  style={{ marginLeft: '0.5em' }}
                >
                  <Icon name={showThinking ? 'eye slash' : 'eye'} />
                  {showThinking ? 'Hide' : 'Show'} Analysis
                </Button>
              )}
              {/* {metadata?.saved && (
                <Label size="mini" color="green" style={{ marginLeft: '0.5em' }}>
                  <Icon name="check" />
                  Saved
                </Label>
              )} */}
            </Comment.Metadata>

            {/* Show thinking process if toggled */}
            {showThinking && metadata?.thinking && (
              <Message info style={{ margin: '0.5em 0' }}>
                <Message.Header>
                  {metadata?.summary ? 'Analysis Process' : 'Thinking Process'}
                </Message.Header>
                <div style={{
                  whiteSpace: 'pre-wrap',
                  fontSize: '0.9em',
                  maxHeight: '300px',
                  overflowY: 'auto',
                  fontFamily: 'monospace'
                }}>
                  {metadata.thinking}
                </div>
              </Message>
            )}

            <Comment.Text>
              <div className="markdown-content">
                {content && (
                  <>
                    <Markdown rehypePlugins={[rehypeRaw]} remarkPlugins={[remarkGfm]}>{content}</Markdown>
                  </>
                )}
                {metadata?.streaming && !content && (
                  <div style={{ fontStyle: 'italic', color: '#888' }}>
                    {metadata?.summary ? (
                      <>
                        <Icon name="clipboard list" />
                        Generating patient summary...
                      </>
                    ) : (
                      <>
                        <Icon name="brain" />
                        Thinking...
                      </>
                    )}
                  </div>
                )}
                {metadata?.streaming && content && (
                  <span className="streaming-cursor"></span>
                )}
              </div>
            </Comment.Text>
          </Comment.Content>
        </Comment>
      );

    case 'system':
      return (
        <Message
          info={!metadata?.error}
          warning={metadata?.error}
          size="tiny"
          className="system-message"
        >
          <Message.Header>{metadata?.action ? 'System Action' : 'System Message'}</Message.Header>
          <p>{content}</p>
          <span className="timestamp">{formattedTime}</span>
        </Message>
      );

    default:
      return null;
  }
};

// Helper function to render markdown-like content (same as before)
const renderMarkdownContent = (content: string) => {
  // Your existing markdown rendering logic
  const paragraphs = content.split('\n\n');

  return paragraphs.map((paragraph, i) => {
    if (paragraph.trim().match(/^[*-]\s/)) {
      const listItems = paragraph.split(/\n[*-]\s/).filter(Boolean);
      return (
        <ul key={i}>
          {listItems.map((item, j) => (
            <li key={j}>{item}</li>
          ))}
        </ul>
      );
    }

    if (paragraph.startsWith('# ')) {
      return <h3 key={i}>{paragraph.substring(2)}</h3>;
    }

    if (paragraph.startsWith('## ')) {
      return <h4 key={i}>{paragraph.substring(3)}</h4>;
    }

    const boldText = paragraph.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    const formattedText = boldText.replace(/\*(.*?)\*/g, '<em>$1</em>');

    return <p key={i} dangerouslySetInnerHTML={{ __html: formattedText }} />;
  });
};

export default ChatMessage;